import { useCallback, useEffect, useRef, useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { useIdleTimer } from '@alchemer/ui/hooks/use-idle-timer';
import { useBoundStore } from '@/store';
import { AUTH_CONFIG } from '@/lib/auth0';
import { setAuthTokenAccessor } from '@/api/mutators/IgniteAxiosClient';
import { setAuthTokenAccessor as setGlobalAuthTokenAccessor } from '@/api/mutators/GlobalAxiosClient';

/** Log out after 2 hours of inactivity (override via VITE_IDLE_TIMEOUT_MS) */
const IDLE_TIMEOUT_MS = Number(import.meta.env.VITE_IDLE_TIMEOUT_MS) || 2 * 60 * 60 * 1000;
/** Show warning dialog 5 minutes before logout (override via VITE_IDLE_PROMPT_BEFORE_MS) */
const PROMPT_BEFORE_IDLE_MS = Number(import.meta.env.VITE_IDLE_PROMPT_BEFORE_MS) || 5 * 60 * 1000;
/** Refresh Auth0 token every 10 minutes while actively interacting (override via VITE_AUTH0_TOKEN_REFRESH_MS) */
const TOKEN_REFRESH_MS = Number(import.meta.env.VITE_AUTH0_TOKEN_REFRESH_MS) || 10 * 60 * 1000;
/** Countdown tick interval */
const COUNTDOWN_INTERVAL_MS = 1000;

/**
 * Manages the full inactivity lifecycle for the dashboard:
 *
 * 1. **Idle detection** — tracks user activity, triggers logout after {@link IDLE_TIMEOUT_MS}
 * 2. **Warning prompt** — shows a dialog {@link PROMPT_BEFORE_IDLE_MS} before logout with a countdown
 * 3. **Cross-tab sync** — shares idle state across browser tabs via react-idle-timer
 * 4. **Iframe activity** — detects ThoughtSpot iframe interactions via postMessage
 * 5. **Token refresh** — refreshes the Auth0 access token every {@link TOKEN_REFRESH_MS} while the user is active
 *
 * @param onLogout - Callback invoked when the user should be logged out (idle timeout or manual)
 */
export function useInactivityLogout(onLogout: () => Promise<void>) {
  const { getAccessTokenSilently } = useAuth0();
  const thoughtspotUrl = useBoundStore((state) => state.getCurrentThoughtspotUrl());
  const [isWarningOpen, setIsWarningOpen] = useState(false);
  const [remainingSeconds, setRemainingSeconds] = useState(0);
  const countdownRef = useRef<ReturnType<typeof setInterval>>(null);

  const onPrompt = useCallback(() => {
    setIsWarningOpen(true);
  }, []);

  const onIdle = useCallback(() => {
    setIsWarningOpen(false);
    onLogout();
  }, [onLogout]);

  const onActive = useCallback(() => {
    setIsWarningOpen(false);
  }, []);

  const { getRemainingTime, activate, getLastActiveTime } = useIdleTimer({
    timeout: IDLE_TIMEOUT_MS,
    promptBeforeIdle: PROMPT_BEFORE_IDLE_MS,
    onPrompt,
    onIdle,
    onActive,
    crossTab: true,
    syncTimers: 200,
  });

  // Countdown timer — ticks every second while the warning dialog is open
  useEffect(() => {
    if (isWarningOpen) {
      setRemainingSeconds(Math.ceil(getRemainingTime() / 1000));
      countdownRef.current = setInterval(() => {
        setRemainingSeconds(Math.ceil(getRemainingTime() / 1000));
      }, COUNTDOWN_INTERVAL_MS);
    }

    return () => {
      if (countdownRef.current) {
        clearInterval(countdownRef.current);
        countdownRef.current = null;
      }
    };
  }, [isWarningOpen, getRemainingTime]);

  // ThoughtSpot iframe activity — DOM events inside a cross-origin iframe don't
  // bubble to the parent document, so we listen for postMessage from the TS origin
  // and signal activity to the idle timer via activate()
  useEffect(() => {
    if (!thoughtspotUrl) return;
    const origin = new URL(thoughtspotUrl).origin;
    const handleIframeMessage = (e: MessageEvent) => {
      if (e.origin === origin) activate();
    };
    window.addEventListener('message', handleIframeMessage);
    return () => window.removeEventListener('message', handleIframeMessage);
  }, [thoughtspotUrl, activate]);

  // Auth0 session refresh — keeps the session alive by fetching a fresh access
  // token periodically. Only refreshes if the user was active within the last
  // TOKEN_REFRESH_MS window (avoids unnecessary calls when the user walked away
  // but hasn't hit the idle timeout yet)
  useEffect(() => {
    const interval = setInterval(async () => {
      const lastActive = getLastActiveTime();
      const recentlyActive = lastActive && Date.now() - lastActive.getTime() < TOKEN_REFRESH_MS;
      if (recentlyActive) {
        try {
          const token = await getAccessTokenSilently({ authorizationParams: AUTH_CONFIG, cacheMode: 'off' });
          setAuthTokenAccessor(() => Promise.resolve(token));
          setGlobalAuthTokenAccessor(() => Promise.resolve(token));
        } catch {
          // Silent failure — next interval will retry
        }
      }
    }, TOKEN_REFRESH_MS);
    return () => clearInterval(interval);
  }, [getAccessTokenSilently, getLastActiveTime]);

  /** Dismisses the warning dialog and resets the idle timer */
  const stayActive = useCallback(() => {
    activate();
    setIsWarningOpen(false);
  }, [activate]);

  return { isWarningOpen, remainingSeconds, stayActive };
}
